'use client'

import type { PropsWithChildren } from 'react'

import { type ThemeBackground, themeBackgroundMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { isDefined } from '@mntn-dev/utilities'

import { type UseSizingProps, useSizing } from '../../hooks/use-sizing.ts'
import { getTestProps, type TestIds } from '../../utils/testing.ts'
import { Divider } from '../divider/divider.tsx'
import { useSurfaceContext } from './use-surface.ts'

type SurfaceSectionProps = Readonly<
  PropsWithChildren<
    {
      className?: string
      backgroundColor?: ThemeBackground
      divide?: boolean
      onClick?: () => void
    } & UseSizingProps &
      TestIds
  >
>

const SurfaceSection = ({
  backgroundColor = 'transparent',
  className,
  children,
  dataTestId,
  dataTrackingId,
  divide: divideProp,
  onClick,
  ...props
}: SurfaceSectionProps) => {
  const {
    hasSurfaceChildren,
    paddingClassName,
    setHasSurfaceChildren,
    divide: divideContext,
  } = useSurfaceContext()

  const divide = isDefined(divideProp) ? divideProp : divideContext

  const setRef = () => {
    if (!hasSurfaceChildren) {
      setHasSurfaceChildren(true)
    }
  }

  const sizing = useSizing({
    ...props,
  })

  return (
    <div
      ref={setRef}
      className={cn(
        'relative group',
        paddingClassName,
        !divide && 'not-last:pb-0',
        themeBackgroundMap[backgroundColor],
        ...Object.values(sizing),
        className
      )}
      {...getTestProps({ dataTestId, dataTrackingId })}
      onClick={onClick}
    >
      {children}
      {divide && (
        <div className="absolute bottom-0 left-0 right-0 h-px group-last:hidden">
          <Divider />
        </div>
      )}
    </div>
  )
}

export { SurfaceSection, type SurfaceSectionProps }
